import { defineStore } from 'pinia';
import { ElMessage } from 'element-plus';
import { loginApi, getUserInfoApi, logoutApi, type UserInfoResponse } from '@/api/auth';
import { i18n } from '@/locales';
import { UserRole } from '@/router/config/roles';

interface UserState {
  token: string;
  roles: string[];
  permissions: string[];
  roleId?: string | number;
  profile: UserInfoResponse | null;
  productId: string;
  productIds: number[];
}

// 从cookie获取token的函数
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

// 获取有效token的函数：优先从cookie获取，如果没有则从localStorage获取
function getValidToken(): string {
  const cookieToken = getCookie('access_token');
  if (cookieToken) {
    return cookieToken;
  }
  // 否则从localStorage获取
  return '';
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: getValidToken(),
    productId: localStorage.getItem('product-id') || '',
    roles: [],
    roleId: '',
    permissions: [],
    profile: null,
    productIds: (() => {
      const storedProductIds = localStorage.getItem('productIds');
      if (storedProductIds) {
        try {
          return JSON.parse(storedProductIds);
        } catch (error) {
          localStorage.removeItem('productIds');
          return [];
        }
      }
      return [];
    })()
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    hasRole: (state) => (role: string) => state.roles.includes(role),
    hasPermission: (state) => (permission: string) => state.permissions.includes(permission),
    userName: (state) => state.profile?.name || state.profile?.username || '',
    userEmail: (state) => state.profile?.email || '',
    userAvatar: (state) => state.profile?.avatar || '',
    userEnterpriseId: (state) => state.profile?.enterpriseId || '',
    hasAnyProductPermission: (state) => state.productIds.length > 0,
    hasMultiProductPermission: (state) => state.productIds.length > 1,
    productIdsList: (state) => state.productIds,
  },

  actions: {
    // 登录
    async login(params: { username: string; password: string }) {
      try {
        const response = await loginApi(params);

        // 保存 token
        this.token = response.accessToken;

        localStorage.setItem('lincId', response.lincId);

        // 将token写入cookie，设置为父域、Secure、SameSite=None
        this.setTokenToCookie(response.accessToken);

        // 解析 accessToken 的第二段（payload 部分）
        const tokenParts = response.accessToken.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        // 解码 JWT payload 部分
        const payloadBase64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decodedPayload = atob(payloadBase64);
        const tokenPayload = JSON.parse(decodedPayload);

        // 检查 product_ids 数组并去重
        const productIds = tokenPayload.product_ids || [];
        const uniqueProductIds = [...new Set(productIds)] as number[];

        if (uniqueProductIds.length === 0) {
          this.clearAll();
          throw new Error(i18n.global.t('product.noAccessPermission'));
        }

        // 设置产品ID列表
        this.setProductIds(uniqueProductIds);

        // 从 JWT payload 中提取用户信息并设置到状态中
        if (tokenPayload.user_id) {
          if (!this.profile) {
            this.profile = {} as UserInfoResponse;
          }
          this.profile.id = tokenPayload.user_id;
          this.profile.email = tokenPayload.email;
          this.profile.enterpriseId = tokenPayload.enterprise_id;
          this.profile.name = tokenPayload.sub?.split('@')[0] || tokenPayload.email; // 从邮箱中提取用户名
        }

        // 设置角色信息
        if (tokenPayload.roles && Array.isArray(tokenPayload.roles)) {
          this.roles = tokenPayload.roles;
        }

        // 检查是否有产品权限，如果有才显示登录成功提示
        if (this.productIds && this.productIds.length > 0) {
          ElMessage.success(i18n.global.t('login.loginSuccess'));
        }

        return response;
      } catch (error: any) {
        ElMessage.error(error.message || i18n.global.t('login.loginFailed'));
        throw error;
      }
    },
    getCurRole(roles: any[]) {
      let curPId = this.productId;
      let curRole;
      roles.map(item => {
        if (item.productId == curPId) {
          curRole = item;
        }
      })
      return curRole || roles[0]
    },

    // 获取用户信息
    async getUserInfo() {
      try {
        console.log('userInfo')
        if (!this.token) {
          throw new Error('未登录');
        }

        const profileDetail = await getUserInfoApi();
        let resRoles = profileDetail.roles || [];
        let userRole: Account.RoleInfo = this.getCurRole(resRoles) as Account.RoleInfo;
        
        const userInfo: UserInfoResponse = {
          id: profileDetail.id,
          username: profileDetail.name,
          email: profileDetail.email,
          name: profileDetail.name,
          position: profileDetail.position,
          enterpriseId: profileDetail.enterpriseId,
          roles: [],
          roleInfo: {
            id: userRole.id, // 根据实际情况映射角色
            name: userRole.name
          },
          status: 0,
          ifRoot: 0,
          createBy: 0,
          updateBy: 0,
          createTime: '',
          updateTime: ''
        };

        console.log('userInfo', userInfo)
        this.profile = userInfo;
        this.roles = [userInfo.roleInfo?.name || UserRole.SUPER_ADMIN];
        this.roleId = userRole.id || UserRole.SUPER_ADMIN;
        return userInfo;
      } catch (error: any) {
        // 如果获取用户信息失败，可能是 token 过期
        if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
          this.logout();
        }
        throw error;
      }
    },

    // 登出
    async logout() {
      try {
        // 调用登出接口
        if (this.token) {
          await logoutApi();
        }
      } catch (error) {
        console.error('登出接口调用失败:', error);
      } finally {
        // 清空用户状态
        this.clearAll();
      }
    },

    clearAll() {
      this.token = '';
      this.roles = [];
      this.permissions = [];
      this.profile = null;

      // 清空本地存储
      localStorage.removeItem('lincId');
      localStorage.removeItem('product-id');

      // 清除cookie中的token
      this.clearTokenFromCookie();
    },

    handleTokenExpired() {
      this.clearAll();
      window.location.href = '/login';
    },

    // 设置用户产品ID列表
    setProductIds(productIds: number[]) {
      this.productIds = productIds;
      if (productIds.length > 0) {
        localStorage.setItem('productIds', JSON.stringify(productIds));
      } else {
        localStorage.removeItem('productIds');
      }
    },

    // 从cookie中清除token
    clearTokenFromCookie() {
      // 获取当前域名并提取父域
      const domainParts = window.location.hostname.split('.');
      let parentDomain = '';

      // 如果是 localhost 或 IP 地址，不设置 domain
      if (window.location.hostname === 'localhost' || /^(\d+\.){3}\d+$/.test(window.location.hostname)) {
        document.cookie = 'access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None';
      } else {
        // 对于生产环境，获取父域名
        if (domainParts.length >= 2) {
          // 取最后两部分作为父域名，例如：对于 test.linccareer.com，取 linccareer.com
          parentDomain = domainParts.slice(-2).join('.');
          document.cookie = `access_token=; Domain=.${parentDomain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None`;
        } else {
          // 如果域名部分少于2个，直接使用当前域名
          document.cookie = 'access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None';
        }
      }
    },

    // 将token写入cookie，设置为父域、Secure、SameSite=None
    setTokenToCookie(token: string) {
      // 获取当前域名并提取父域
      const domainParts = window.location.hostname.split('.');
      let parentDomain = '';
      const days = 30;
      // 设置过期时间
      const expiresDate = new Date();
      expiresDate.setTime(expiresDate.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `Expires=${expiresDate.toUTCString()}`;
      
      // 或者使用 Max-Age（秒）
      // const maxAge = `Max-Age=${days * 24 * 60 * 60}`;

      if (window.location.hostname === 'localhost' || /^(\d+\.){3}\d+$/.test(window.location.hostname)) {
        document.cookie = `access_token=${token}; ${expires}; Path=/; Secure; SameSite=None`;
      } else {
        if (domainParts.length >= 2) {
          parentDomain = domainParts.slice(-2).join('.');
          document.cookie = `access_token=${token}; ${expires}; Domain=.${parentDomain}; Path=/; Secure; SameSite=None`;
        } else {
          document.cookie = `access_token=${token}; ${expires}; Path=/; Secure; SameSite=None`;
        }
      }
    }
  }
});
